import { useEffect, useRef } from 'react';
import { runScript, runScripts } from '../manager/ScriptManager';
import { ScriptLoadStrategy, ScriptProps } from '../types/types';

type UseScriptOptions = ScriptProps & {
    strategy?: ScriptLoadStrategy;
    onLoad?: () => void;
    onReady?: () => void;
    onError?: (e: Error) => void;
};

export function useScript({
    strategy = 'afterInteractive',
    onLoad,
    onReady,
    onError,
    ...scriptProps
}: UseScriptOptions) {
    const fireReady = useRef(false);

    useEffect(() => {
        let cancelled = false;

        runScript(scriptProps, strategy)
            .then(() => {
                if (cancelled) return;

                onLoad?.();

                if (!fireReady.current) {
                    fireReady.current = true;
                    onReady?.();
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    onError?.(e);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [strategy, scriptProps.id, scriptProps.src, scriptProps.inline]);
}

type UseScriptsOptions = {
    scripts: ScriptProps[];
    strategy?: ScriptLoadStrategy;
    onReady?: () => void;
    onError?: (e: Error) => void;
};

export function useScripts({
    scripts,
    strategy = 'afterInteractive',
    onReady,
    onError,
}: UseScriptsOptions) {
    const fireReady = useRef(false);

    useEffect(() => {
        let cancelled = false;

        if (scripts.length === 0) {
            return () => {
                cancelled = true;
            };
        }

        runScripts(scripts, strategy)
            .then(() => {
                if (cancelled || fireReady.current) return;

                fireReady.current = true;
                onReady?.();
            })
            .catch((e) => {
                if (!cancelled) {
                    onError?.(e);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [strategy, JSON.stringify(scripts.map((s) => s.id ?? s.src ?? s.inline?.slice(0, 16)))]);
}
